import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/auth';

const AdminCompositions = () => {
  const [compositions, setCompositions] = useState([]);
  const [stats, setStats] = useState({ total: 0, published: 0, draft: 0, cancelled: 0 });
  const [classes, setClasses] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [editingCompo, setEditingCompo] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClass, setFilterClass] = useState('all');
  const [formData, setFormData] = useState({
    class_id: '',
    subject_id: '',
    title: '',
    date: '',
    start_time: '',
    end_time: '',
    room: '',
    coefficient: 2,
    max_score: 20,
    description: '',
    topics: '',
    supervisor_id: '',
    status: 'draft',
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [compoRes, statsRes, classRes, subjRes, assignRes, teacherRes] = await Promise.all([
        api.get('/compositions', { headers }),
        api.get('/compositions/stats', { headers }),
        api.get('/classes', { headers }),
        api.get('/subjects', { headers }),
        api.get('/assignments', { headers }),
        api.get('/teachers', { headers }),
      ]);

      setCompositions(Array.isArray(compoRes.data) ? compoRes.data : []);
      setStats(statsRes.data || {});
      setClasses(Array.isArray(classRes.data) ? classRes.data : []);
      setAllSubjects(Array.isArray(subjRes.data) ? subjRes.data : []);
      setAssignments(Array.isArray(assignRes.data) ? assignRes.data : []);
      setTeachers(Array.isArray(teacherRes.data) ? teacherRes.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Filtre : matières disponibles pour la classe sélectionnée
  const availableSubjects = useMemo(() => {
    if (!formData.class_id) return [];
    const subjectIdsForClass = assignments
      .filter((a) => String(a.class_id) === String(formData.class_id))
      .map((a) => a.subject_id);
    return allSubjects.filter((s) => subjectIdsForClass.includes(s.id));
  }, [formData.class_id, assignments, allSubjects]);

  // ✅ Quand on change de classe, on reset la matière si elle n'est plus valide
  useEffect(() => {
    if (formData.class_id && formData.subject_id) {
      const stillValid = availableSubjects.some((s) => String(s.id) === String(formData.subject_id));
      if (!stillValid) {
        setFormData((prev) => ({ ...prev, subject_id: '' }));
      }
    }
  }, [formData.class_id, availableSubjects]);

  const filteredCompositions = useMemo(() => {
    return compositions.filter((c) => {
      if (searchTerm) {
        const t = searchTerm.toLowerCase();
        const title = (c.title || '').toLowerCase();
        const cls = (c.class_name || '').toLowerCase();
        const subj = (c.subject || '').toLowerCase();
        if (!title.includes(t) && !cls.includes(t) && !subj.includes(t)) return false;
      }
      if (filterStatus !== 'all' && c.status !== filterStatus) return false;
      if (filterClass !== 'all' && String(c.class_id) !== String(filterClass)) return false;
      return true;
    });
  }, [compositions, searchTerm, filterStatus, filterClass]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingCompo) {
        await api.put(`/compositions/${editingCompo.id}`, formData, { headers });
      } else {
        await api.post('/compositions', formData, { headers });
      }

      setShowModal(false);
      setEditingCompo(null);
      resetForm();
      fetchAll();
    } catch (err) {
      console.error('Erreur:', err);
      alert(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (compo) => {
    setEditingCompo(compo);
    setFormData({
      class_id: compo.class_id || '',
      subject_id: compo.subject_id || '',
      title: compo.title || '',
      date: compo.date || '',
      start_time: compo.start_time || '',
      end_time: compo.end_time || '',
      room: compo.room || '',
      coefficient: compo.coefficient || 2,
      max_score: compo.max_score || 20,
      description: compo.description || '',
      topics: compo.topics || '',
      supervisor_id: compo.supervisor_id || '',
      status: compo.status || 'draft',
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingCompo(null);
    resetForm();
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      class_id: '',
      subject_id: '',
      title: '',
      date: '',
      start_time: '',
      end_time: '',
      room: '',
      coefficient: 2,
      max_score: 20,
      description: '',
      topics: '',
      supervisor_id: '',
      status: 'draft',
    });
  };

  const handlePublish = async (id) => {
    if (!window.confirm('Publier cette composition ? Elle sera visible par les élèves et parents.')) return;
    try {
      const token = localStorage.getItem('token');
      await api.post(`/compositions/${id}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchAll();
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur lors de la publication');
    }
  };

  const openCancelModal = (compo) => {
    setCancelTarget(compo);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      alert('Veuillez indiquer un motif');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await api.post(`/compositions/${cancelTarget.id}/cancel`, { reason: cancelReason }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowCancelModal(false);
      setCancelTarget(null);
      fetchAll();
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur lors de l\'annulation');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement ce brouillon ?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/compositions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchAll();
    } catch (err) {
      console.error('Erreur:', err);
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">🟢 Publiée</span>;
      case 'draft':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">🟡 Brouillon</span>;
      case 'cancelled':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">🔴 Annulée</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{status}</span>;
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleDateString('fr-FR');
    } catch {
      return d;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">🧪 Gestion des compositions</h1>
          <p className="text-gray-500">Planifiez et publiez les compositions de l'établissement</p>
        </div>
        <button
          onClick={handleNew}
          className="bg-[#E67E22] text-white px-4 py-2 rounded-lg hover:bg-[#D35400] transition font-medium"
        >
          ➕ Planifier une composition
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-blue-500">
          <p className="text-xs text-gray-500 mb-1">📋 Total</p>
          <p className="text-2xl font-bold text-[#2C3E50]">{stats.total || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-green-500">
          <p className="text-xs text-gray-500 mb-1">🟢 Publiées</p>
          <p className="text-2xl font-bold text-green-600">{stats.published || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-yellow-500">
          <p className="text-xs text-gray-500 mb-1">🟡 Brouillons</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.draft || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-red-500">
          <p className="text-xs text-gray-500 mb-1">🔴 Annulées</p>
          <p className="text-2xl font-bold text-red-600">{stats.cancelled || 0}</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="text"
            placeholder="🔍 Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          />
          <select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          >
            <option value="all">Toutes les classes</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          >
            <option value="all">Tous les statuts</option>
            <option value="draft">🟡 Brouillon</option>
            <option value="published">🟢 Publiée</option>
            <option value="cancelled">🔴 Annulée</option>
          </select>
          <div className="text-sm text-gray-500 flex items-center px-2">
            {filteredCompositions.length} composition(s)
          </div>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      ) : filteredCompositions.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <p className="text-4xl mb-3">📭</p>
          <p className="text-lg font-medium text-gray-700 mb-2">Aucune composition</p>
          <p className="text-sm text-gray-500 mb-4">
            {compositions.length === 0
              ? 'Commencez par planifier votre première composition.'
              : 'Aucun résultat pour ces filtres.'}
          </p>
          {compositions.length === 0 && (
            <button
              onClick={handleNew}
              className="bg-[#E67E22] text-white px-4 py-2 rounded-lg hover:bg-[#D35400] transition"
            >
              ➕ Planifier une composition
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredCompositions.map((c) => (
            <div key={c.id} className="bg-white rounded-xl shadow-md overflow-hidden border-l-4 border-[#E67E22]">
              <div className="p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-bold text-[#2C3E50]">{c.title}</h3>
                      {getStatusBadge(c.status)}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-600">
                      <div>🏫 <strong>{c.class_name || '-'}</strong></div>
                      <div>📚 <strong>{c.subject || '-'}</strong></div>
                      <div>📅 <strong>{formatDate(c.date)}</strong></div>
                      <div>
                        ⏰ <strong>
                          {c.start_time && c.end_time ? `${c.start_time} - ${c.end_time}` : '-'}
                        </strong>
                      </div>
                      {c.room && <div>🏛️ Salle : <strong>{c.room}</strong></div>}
                      <div>⚖️ Coef : <strong>{c.coefficient}</strong></div>
                      {c.teacher_name && <div>👨‍🏫 <strong>{c.teacher_name}</strong></div>}
                      {c.supervisor_name && <div>👁️ <strong>{c.supervisor_name}</strong></div>}
                    </div>
                    {c.topics && (
                      <p className="text-xs text-gray-500 mt-2">
                        📖 <strong>Programme :</strong> {c.topics}
                      </p>
                    )}
                    {c.status === 'cancelled' && c.cancellation_reason && (
                      <p className="text-xs text-red-600 mt-2">
                        ❌ <strong>Motif :</strong> {c.cancellation_reason}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    {c.status === 'draft' && (
                      <>
                        <button
                          onClick={() => handlePublish(c.id)}
                          className="text-xs bg-green-50 text-green-700 px-3 py-1.5 rounded-lg hover:bg-green-100 transition font-medium whitespace-nowrap"
                        >
                          📢 Publier
                        </button>
                        <button
                          onClick={() => handleEdit(c)}
                          className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition font-medium"
                        >
                          ✏️ Modifier
                        </button>
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="text-xs bg-red-50 text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-100 transition font-medium"
                        >
                          🗑️ Supprimer
                        </button>
                      </>
                    )}
                    {c.status === 'published' && (
                      <button
                        onClick={() => openCancelModal(c)}
                        className="text-xs bg-red-50 text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-100 transition font-medium whitespace-nowrap"
                      >
                        🚫 Annuler
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal créer/modifier */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl my-8">
            <h2 className="text-xl font-bold text-[#2C3E50] mb-4">
              {editingCompo ? '✏️ Modifier la composition' : '🧪 Nouvelle composition'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Classe *</label>
                  <select
                    value={formData.class_id}
                    onChange={(e) => setFormData({ ...formData, class_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                    required
                    disabled={!!editingCompo}
                  >
                    <option value="">-- Choisir une classe --</option>
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Matière *</label>
                  <select
                    value={formData.subject_id}
                    onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22] disabled:bg-gray-100"
                    required
                    disabled={!formData.class_id || !!editingCompo}
                  >
                    <option value="">-- Choisir une matière --</option>
                    {availableSubjects.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  {formData.class_id && availableSubjects.length === 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      ⚠️ Aucune matière affectée à cette classe. Créez d'abord une affectation.
                    </p>
                  )}
                  {formData.class_id && availableSubjects.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      ✅ {availableSubjects.length} matière(s) disponible(s)
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Composition de Mathématiques - 1er trimestre"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heure début</label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heure fin</label>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salle</label>
                  <input
                    type="text"
                    value={formData.room}
                    onChange={(e) => setFormData({ ...formData, room: e.target.value })}
                    placeholder="Ex: Salle 12"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coefficient *</label>
                  <input
                    type="number"
                    step="0.5"
                    value={formData.coefficient}
                    onChange={(e) => setFormData({ ...formData, coefficient: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note max *</label>
                  <input
                    type="number"
                    value={formData.max_score}
                    onChange={(e) => setFormData({ ...formData, max_score: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Surveillant</label>
                <select
                  value={formData.supervisor_id}
                  onChange={(e) => setFormData({ ...formData, supervisor_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                >
                  <option value="">Aucun</option>
                  {teachers.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Chapitres à réviser</label>
                <textarea
                  value={formData.topics}
                  onChange={(e) => setFormData({ ...formData, topics: e.target.value })}
                  rows="2"
                  placeholder="Ex: Fractions, Nombres décimaux, Calcul littéral"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="2"
                  placeholder="Ex: Calculatrice interdite"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                />
              </div>

              {!editingCompo && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Enregistrer comme</label>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="draft"
                        checked={formData.status === 'draft'}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      />
                      <span className="text-sm">🟡 Brouillon</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="published"
                        checked={formData.status === 'published'}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      />
                      <span className="text-sm">🟢 Publier immédiatement</span>
                    </label>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingCompo(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={!formData.class_id || !formData.subject_id}
                  className="px-4 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] transition font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {editingCompo ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal annulation */}
      {showCancelModal && cancelTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-red-700 mb-4">🚫 Annuler la composition</h2>
            <p className="text-sm text-gray-600 mb-4">
              Vous allez annuler : <strong>{cancelTarget.title}</strong>
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motif *</label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows="3"
              placeholder="Ex: Professeur indisponible"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-red-500"
            />
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => { setShowCancelModal(false); setCancelTarget(null); }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Retour
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
              >
                Confirmer l'annulation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompositions;